/**
 * HistoryContextBadge — kleines Info-Icon neben einer Email-Subject das einen
 * Popover mit Vergangenheits-Kontext zeigt.
 *
 * Was zeigt es?  Aggregat ueber aehnliche Mails (gleiche `category`) der
 * letzten 180 Tage: wieviele Antworten/Aufgaben/Drafts wurden erstellt,
 * Top-3-Mitarbeiter die Aufgaben bekommen, 3 neueste Beispiele.
 *
 * Feature-Stufe 1 (08.05.2026, Phase 6): nur Plattform-Daten (kausal),
 * keine HERO-Korrelation.
 */
import { useState, useRef, useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { History, X } from "lucide-react"

type Actions = {
  replied: number
  forwarded: number
  task_created: number
  manual_draft_started: number
  plan_executed: number
  contact_linked: number
  only_marked_done: number
}
type Assignee = { name: string; count: number }
type Example = {
  id: number
  received_at: string | null
  subject: string | null
  sender: string | null
  actions_taken: string[]
  task_title?: string | null
  task_user?: string | null
  task_comment?: string | null
  reply_excerpt?: string | null
  handlungsbedarf?: string | null
}
type Response = {
  category: string | null
  match_strategy: "category" | "sender" | "none"
  lookback_days: number
  total_similar: number
  actions: Actions
  top_assignees: Assignee[]
  recent_examples: Example[]
}


export function HistoryContextBadge({ emailId }: { emailId: number }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const { data, isLoading, error } = useQuery<Response>({
    queryKey: ["email-history-context", emailId],
    queryFn: async () => {
      const res = await fetch(`/api/v1/emails/${emailId}/history-context`, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    enabled: open,  // lazy: erst laden wenn der User klickt
    staleTime: 60_000,
  })

  // Click-outside zum Schliessen
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onDoc)
    return () => document.removeEventListener("mousedown", onDoc)
  }, [open])

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium transition"
        style={{ background: "#F5F4F0", color: "#5F5E5A", border: "1px solid rgba(44,44,42,0.12)" }}
        title="Was wurde bei aehnlichen Mails gemacht?"
      >
        <History className="w-3 h-3" />
        Historie
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-30 w-[420px] rounded-lg border p-4"
          style={{ background: "#ffffff", color: "#2C2C2A", boxShadow: "0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)", borderColor: "rgba(44,44,42,0.15)" }}
        >
          <div className="flex items-start justify-between gap-2 mb-3">
            <div>
              <div className="text-[13px] font-semibold" style={{ color: "#2C2C2A" }}>Vergangenheits-Kontext</div>
              {data && (
                <div className="text-[11px] mt-0.5" style={{ color: "#5F5E5A" }}>
                  {data.match_strategy === "category" && data.category && (
                    <>Aehnliche Mails der Kategorie <strong>{data.category}</strong> · letzte {data.lookback_days} Tage</>
                  )}
                  {data.match_strategy === "sender" && (
                    <>Mails vom selben Absender · letzte {data.lookback_days} Tage</>
                  )}
                  {data.match_strategy === "none" && <>Kein Aehnlichkeits-Match (keine Kategorie, kein Sender)</>}
                </div>
              )}
            </div>
            <button onClick={() => setOpen(false)} style={{ color: "#888780" }}>
              <X className="w-4 h-4" />
            </button>
          </div>

          {isLoading && <div className="text-[12px]" style={{ color: "#5F5E5A" }}>Laed…</div>}
          {error && <div className="text-[12px]" style={{ color: "#dc2626" }}>Fehler: {String(error)}</div>}

          {data && data.total_similar === 0 && (
            <div className="text-[12px] italic" style={{ color: "#5F5E5A" }}>
              Keine aehnlichen Mails im Zeitraum gefunden — du startest hier ohne Pattern.
            </div>
          )}

          {data && data.total_similar > 0 && (
            <>
              <div className="text-[12px] mb-3" style={{ color: "#2C2C2A" }}>
                <strong>{data.total_similar} aehnliche Mail{data.total_similar === 1 ? "" : "en"}</strong> gefunden.
              </div>
              <ActionRows actions={data.actions} total={data.total_similar} />

              {data.top_assignees.length > 0 && (
                <div className="mt-3 pt-3" style={{ borderTop: "1px solid rgba(44,44,42,0.12)" }}>
                  <div className="text-[11px] uppercase tracking-wide font-semibold mb-1.5" style={{ color: "#5F5E5A" }}>
                    Aufgaben gingen an
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {data.top_assignees.map((a) => (
                      <span key={a.name} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[12px]" style={{ background: "#F5F4F0", color: "#2C2C2A" }}>
                        {a.name} <span style={{ color: "#5F5E5A" }}>×{a.count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {data.recent_examples.length > 0 && (
                <div className="mt-3 pt-3" style={{ borderTop: "1px solid rgba(44,44,42,0.12)" }}>
                  <div className="text-[11px] uppercase tracking-wide font-semibold mb-1.5" style={{ color: "#5F5E5A" }}>
                    Was wurde konkret gemacht?
                  </div>
                  <div className="space-y-2.5">
                    {data.recent_examples.map((ex) => (
                      <div key={ex.id} className="text-[12px] rounded p-2" style={{ background: "#FAFAF7", border: "1px solid rgba(44,44,42,0.08)" }}>
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                          <a href={`/emails/${ex.id}`} className="font-medium hover:underline" style={{ color: "#3C3489" }}>
                            #{ex.id}
                          </a>
                          <span style={{ color: "#888780" }} className="text-[11px]">
                            {(ex.received_at || "").slice(0, 10)}
                          </span>
                        </div>
                        <div className="text-[11.5px] mb-1" style={{ color: "#2C2C2A", fontWeight: 500 }}>{ex.subject}</div>

                        {ex.task_title && (
                          <div className="mt-1.5 pl-2" style={{ borderLeft: "2px solid #6d28d9" }}>
                            <div className="text-[10px] uppercase tracking-wide" style={{ color: "#6d28d9", fontWeight: 600 }}>
                              {ex.task_user ? `Aufgabe → ${ex.task_user}` : "Aufgabe erstellt"}
                            </div>
                            <div style={{ color: "#2C2C2A" }} className="text-[12px]">{ex.task_title}</div>
                            {ex.task_comment && (
                              <div className="text-[11px] mt-0.5 italic" style={{ color: "#5F5E5A" }}>
                                {ex.task_comment}
                              </div>
                            )}
                          </div>
                        )}

                        {ex.reply_excerpt && (
                          <div className="mt-1.5 pl-2" style={{ borderLeft: "2px solid #047857" }}>
                            <div className="text-[10px] uppercase tracking-wide" style={{ color: "#047857", fontWeight: 600 }}>
                              Antwort gesendet
                            </div>
                            <div className="text-[11px] mt-0.5" style={{ color: "#2C2C2A", whiteSpace: "pre-wrap" }}>
                              {ex.reply_excerpt}
                            </div>
                          </div>
                        )}

                        {ex.handlungsbedarf && (
                          <div className="mt-1.5 pl-2" style={{ borderLeft: "2px solid #b45309" }}>
                            <div className="text-[10px] uppercase tracking-wide" style={{ color: "#b45309", fontWeight: 600 }}>
                              KI-Handlungsbedarf damals
                            </div>
                            <div className="text-[11px] mt-0.5 italic" style={{ color: "#5F5E5A" }}>
                              {ex.handlungsbedarf}
                            </div>
                          </div>
                        )}

                        {!ex.task_title && !ex.reply_excerpt && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {ex.actions_taken.map((a, i) => (
                              <span key={i} className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: "#dbeafe", color: "#1d4ed8" }}>
                                {a}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warning wenn nichts substantielles passiert ist */}
              {data.actions.replied === 0 &&
                data.actions.forwarded === 0 &&
                data.actions.task_created === 0 &&
                data.actions.plan_executed === 0 && (
                <div className="mt-3 pt-3 text-[11px] rounded p-2" style={{ borderTop: "1px solid rgba(44,44,42,0.12)", background: "#fef3c7", color: "#92400e" }}>
                  ⚠️ Kein etabliertes Vorgehen — entscheide selbst, was hier passieren soll.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}


function ActionRows({ actions, total }: { actions: Actions; total: number }) {
  const rows: Array<{ label: string; count: number; color: string }> = [
    { label: "Antwort gesendet", count: actions.replied, color: "#047857" },
    { label: "Weitergeleitet", count: actions.forwarded, color: "#1d4ed8" },
    { label: "Aufgabe erstellt", count: actions.task_created, color: "#6d28d9" },
    { label: "Draft begonnen (nicht gesendet)", count: actions.manual_draft_started, color: "#b45309" },
    { label: "KI-Plan ausgefuehrt", count: actions.plan_executed, color: "#7e22ce" },
    { label: "Nur HERO-Kontakt verknuepft", count: actions.contact_linked, color: "#475569" },
    { label: "Nur als erledigt markiert", count: actions.only_marked_done, color: "#64748b" },
  ]
  return (
    <div className="space-y-1">
      {rows.map((r) => {
        const muted = "#888780"
        const c = r.count > 0 ? r.color : muted
        return (
          <div key={r.label} className="flex items-center justify-between text-[12px]">
            <span style={{ color: c }}>{r.label}</span>
            <span style={{ color: c, fontWeight: r.count > 0 ? 600 : 400 }}>
              {r.count}/{total}
            </span>
          </div>
        )
      })}
    </div>
  )
}
